import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TaxLayout } from "../../components/tax/TaxLayout.js";
import {
  Button,
  Card,
  EmptyState,
  Pill,
  SelectField,
  TextField,
} from "../../components/design-system/index.js";
import { ConfirmButton } from "../../components/confirm-button.js";
import { useToast, Toast } from "../../components/toast.js";
import {
  createTaxDocument,
  deleteTaxDocument,
  listTaxDocuments,
  updateTaxDocument,
} from "../../lib/api-modules/tax.js";
import type {
  TaxDocumentSource,
  TaxDocumentStatus,
  TaxDocumentType,
} from "../../lib/api-modules/types.js";

const DOCUMENT_TYPES: TaxDocumentType[] = [
  "1099-DIV",
  "1099-INT",
  "1099-B",
  "1099-R",
  "1099-NEC",
  "1099-MISC",
  "1099-Q",
  "1099-SA",
  "5498",
  "5498-SA",
  "1098",
  "1098-T",
  "W-2",
  "K-1",
  "other",
];

function statusTone(s: TaxDocumentStatus): "neutral" | "accent" | "success" | "warning" {
  switch (s) {
    case "expected":
      return "warning";
    case "received":
      return "accent";
    case "imported":
      return "accent";
    case "reconciled":
      return "success";
    default:
      return "neutral";
  }
}

export function TaxDocumentsPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const [year, setYear] = useState<string>(String(new Date().getFullYear() - 1));
  const [showAdd, setShowAdd] = useState(false);

  const docs = useQuery({
    queryKey: ["tax-documents", year],
    queryFn: () => listTaxDocuments({ year }),
  });

  const createMut = useMutation({
    mutationFn: createTaxDocument,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tax-documents", year] });
      toast.show("Tax document created");
      setShowAdd(false);
    },
    onError: (err) => toast.show(`Create failed: ${(err).message}`),
  });

  const updateMut = useMutation({
    mutationFn: ({
      year: y,
      doc_id,
      patch,
    }: {
      year: string;
      doc_id: string;
      patch: { status?: TaxDocumentStatus };
    }) => updateTaxDocument(y, doc_id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tax-documents", year] });
      toast.show("Status updated");
    },
    onError: (err) => toast.show(`Update failed: ${(err).message}`),
  });

  const deleteMut = useMutation({
    mutationFn: ({ year: y, doc_id }: { year: string; doc_id: string }) =>
      deleteTaxDocument(y, doc_id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tax-documents", year] });
      toast.show("Document archived");
    },
    onError: (err) => toast.show(`Archive failed: ${(err).message}`),
  });

  return (
    <TaxLayout
      title="Tax documents"
      subtitle="Track 1099 / W-2 / K-1 receipt status. v1: metadata only — file uploads to S3 in Phase 10."
      action={
        <Button onClick={() => setShowAdd((s) => !s)} size="sm">
          {showAdd ? "Cancel" : "Add document"}
        </Button>
      }
    >
      <Card className="p-4 flex items-center gap-3">
        <label className="text-sm text-[var(--color-muted)]" htmlFor="year-input">Year</label>
        <input
          id="year-input"
          type="text"
          value={year}
          onChange={(e) => setYear(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
          className="border border-[var(--color-border-default)] rounded px-2 py-1 text-sm bg-[var(--color-surface)] text-[var(--color-text)] font-mono w-24"
        />
      </Card>

      {showAdd && <CreateDocumentForm year={year} onSubmit={(input) => createMut.mutate(input)} pending={createMut.isPending} />}

      {docs.isPending ? (
        <Card className="p-6 text-sm text-[var(--color-muted)]">Loading…</Card>
      ) : docs.isError ? (
        <Card className="p-6 text-sm text-[var(--color-danger)]">
          Failed to load documents.
        </Card>
      ) : docs.data && docs.data.tax_documents.length > 0 ? (
        <Card className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-[var(--color-muted)] text-left">
              <tr>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Issuer</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Received</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {docs.data.tax_documents.map((d) => (
                <tr key={d.doc_id} className="border-t border-[var(--color-border-subtle)]">
                  <td className="px-3 py-2 font-medium">{d.document_type}</td>
                  <td className="px-3 py-2">{d.issuer ?? "—"}</td>
                  <td className="px-3 py-2 text-xs text-[var(--color-muted)]">{d.source}</td>
                  <td className="px-3 py-2">
                    <Pill tone={statusTone(d.status)}>{d.status}</Pill>
                  </td>
                  <td className="px-3 py-2 text-xs text-[var(--color-muted)]">
                    {d.received_date ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-2">
                      <SelectField
                        label=""
                        value={d.status}
                        onChange={(e) =>
                          updateMut.mutate({
                            year: d.year,
                            doc_id: d.doc_id,
                            patch: {
                              status: e.target.value as TaxDocumentStatus,
                            },
                          })
                        }
                      >
                        <option value="expected">expected</option>
                        <option value="received">received</option>
                        <option value="imported">imported</option>
                        <option value="reconciled">reconciled</option>
                      </SelectField>
                      <ConfirmButton
                        label="Archive"
                        confirmLabel="Confirm archive"
                        pendingLabel="Archiving…"
                        size="xs"
                        isPending={deleteMut.isPending}
                        onConfirm={() =>
                          deleteMut.mutate({
                            year: d.year,
                            doc_id: d.doc_id,
                          })
                        }
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : (
        <EmptyState
          title={`No ${year} tax documents yet`}
          description="Add receipts as they arrive. Mark each one received → imported → reconciled to track CPA-handoff readiness."
        />
      )}
      <Toast message={toast.message} />
    </TaxLayout>
  );
}

function CreateDocumentForm({
  year,
  onSubmit,
  pending,
}: {
  year: string;
  onSubmit: (input: {
    year: string;
    document_type: TaxDocumentType;
    source: TaxDocumentSource;
    status: TaxDocumentStatus;
    issuer?: string;
    received_date?: string;
  }) => void;
  pending: boolean;
}) {
  const [docType, setDocType] = useState<TaxDocumentType>("1099-DIV");
  const [source, setSource] = useState<TaxDocumentSource>("brokerage");
  const [status, setStatus] = useState<TaxDocumentStatus>("expected");
  const [issuer, setIssuer] = useState("");
  const [receivedDate, setReceivedDate] = useState("");

  return (
    <Card className="p-5 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <SelectField
          label="Type"
          value={docType}
          onChange={(e) => setDocType(e.target.value as TaxDocumentType)}
        >
          {DOCUMENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Source"
          value={source}
          onChange={(e) => setSource(e.target.value as TaxDocumentSource)}
        >
          <option value="employer">employer</option>
          <option value="brokerage">brokerage</option>
          <option value="financial_institution">financial_institution</option>
          <option value="manual">manual</option>
        </SelectField>
        <SelectField
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as TaxDocumentStatus)}
        >
          <option value="expected">expected</option>
          <option value="received">received</option>
          <option value="imported">imported</option>
          <option value="reconciled">reconciled</option>
        </SelectField>
        <TextField
          label="Issuer (optional)"
          value={issuer}
          onChange={(e) => setIssuer(e.target.value)}
          placeholder="e.g., Merrill Lynch"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <TextField
          label="Received date (YYYY-MM-DD, optional)"
          value={receivedDate}
          onChange={(e) => setReceivedDate(e.target.value)}
          placeholder="2026-02-15"
        />
        <div className="flex items-end">
          <Button
            onClick={() =>
              onSubmit({
                year,
                document_type: docType,
                source,
                status,
                ...(issuer ? { issuer } : {}),
                ...(receivedDate ? { received_date: receivedDate } : {}),
              })
            }
            disabled={pending}
          >
            {pending ? "Creating…" : "Add document"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
